// client/src/features/fiscal/search.ts — SCR-FISC-001's and SCR-FISC-002's URL state · FISC-007
// (04 §9.2.3, §8.11).
//
// §9.2.3 is NORMATIVE for every LIST screen, and §8.11 adds the one param that is not a filter:
// „SCR-FISC-007 … is an addressable overlay (search param), so any screen showing an
// `ObligationStatus` chip can open it with a shareable URL". `?explain=$obligationId` is
// therefore in BOTH schemas below, and closing the drawer strips only that param — §9.2.3's
// „overlay params compose with filters".
//
// `features/rent/search.ts` is the shape this copies, including its one deliberate omission: no
// Zod `.default()` on a date or a year. A default evaluated at parse time bakes „the year the
// tab was opened" into every URL the router normalises, so a link shared in January would mean
// a different year to whoever opened it, and a test would depend on the wall clock. The window
// is resolved once, explicitly, by the two helpers at the bottom.

import { z } from 'zod';

import { OBLIGATION_STATUSES } from './enums';

/**
 * 03 §13.5's `fiscal_year` is a `smallint`; the seeds go back to 2022 (FISC-001) and nothing
 * downstream can answer for a year the rule table has never held. The bounds are the same ones
 * `features/rent/periods.ts` uses for a period year, for the same reason: a hand-edited URL
 * should be refused by the schema rather than by a 500 four layers down.
 */
const MIN_FISCAL_YEAR = 2000;
const MAX_FISCAL_YEAR = 2100;

/** `YYYY-MM-DD`, the only date shape the wire carries (02 §7.4). */
const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u);

/** SCR-FISC-001 — obligations per owner × fiscal year, plus the addressable drawer. */
export const fiscalObligationsSearchSchema = z.object({
  year: z.coerce.number().int().min(MIN_FISCAL_YEAR).max(MAX_FISCAL_YEAR).optional(),
  ownerId: z.string().uuid().optional(),
  /**
   * Applied to the rows the endpoint returned, not sent to it: `GET /fiscal/obligations` takes
   * `ownerId` and `year` and nothing else. The screen says so where it matters — on the empty
   * state — and D-1274 records what would reverse it.
   */
  status: z.enum(OBLIGATION_STATUSES).optional(),
  explain: z.string().uuid().optional(),
});

export type FiscalObligationsSearch = z.infer<typeof fiscalObligationsSearchSchema>;

/**
 * SCR-FISC-002 — the calendar window and its four server-side filters.
 *
 * The names are the endpoint's own (`from`, `to`, `property`, `form`, `category`, `status`), so
 * the search params ARE the query: a filter the user set is a filter the server applied, and
 * there is no second, client-side notion of „filtered" to disagree with it.
 */
export const fiscalCalendarSearchSchema = z.object({
  from: dateOnly.optional(),
  to: dateOnly.optional(),
  property: z.string().uuid().optional(),
  form: z.string().min(1).max(32).optional(),
  category: z.string().min(1).max(32).optional(),
  status: z.enum(OBLIGATION_STATUSES).optional(),
  explain: z.string().uuid().optional(),
});

export type FiscalCalendarSearch = z.infer<typeof fiscalCalendarSearchSchema>;

/**
 * The fiscal year the obligations list is showing.
 *
 * A landlord opening this screen wants the year they are about to file FOR, which is the year
 * that ended — the D212 for 2025 income is due in 2026 (01 §18). So the default is the
 * PREVIOUS calendar year, not the current one, and it is resolved from `now` here rather than
 * baked into the schema.
 */
export function resolveFiscalYear(search: FiscalObligationsSearch, now: Date): number {
  return search.year ?? now.getFullYear() - 1;
}

/**
 * The window the calendar is showing: today, and the twelve months after it.
 *
 * 366 days is the server's own bound on one request (FISC-003), so twelve months is the widest
 * window that never needs a second call. Both ends are written into the URL the moment the user
 * navigates, so a shared link is a shared window.
 */
export function resolveCalendarWindow(
  search: FiscalCalendarSearch,
  now: Date,
): { from: string; to: string } {
  const today = isoDate(now);
  const horizon = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());

  return {
    from: search.from ?? today,
    to: search.to ?? isoDate(horizon),
  };
}

/**
 * A `Date` as the wire's `YYYY-MM-DD`, in LOCAL parts.
 *
 * `toISOString()` would be wrong here and the difference is a whole day: it converts to UTC, so
 * a Bucharest user opening the calendar at 01:00 on 1 March would be handed 28 February. The
 * calendar's dates are Romanian business dates (02 §9.2), and the browser's local calendar is
 * the closest thing the client has to one.
 */
function isoDate(value: Date): string {
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${value.getFullYear()}-${month}-${day}`;
}
